import { ReactElement, useMemo } from 'react'
import { Form2 } from '@ui/forms'
import { useModelStore, useTransmissionStore, useStiffeningStore } from '@editorStores'
import TensileTransmitterListFields from './components/TensileTransmitterListFields'
import { connectorListSchema } from './schema'
import { TensileTransmitterFormValues } from './types'
import { applyTensileFormValues, toTensileFormValues } from './utils/graph'

interface Props {
  selectedElement: string
  /** Receives the edited graph, the store is only updated once it has been saved */
  onSave: (graph: TensileTransmissionGraph) => void
  onRecalculate: () => void
  isLoading: boolean
  isRecalculating: boolean
}

const TensileTransmitterList = ({
  selectedElement,
  onSave,
  isLoading,
  isRecalculating,
  onRecalculate,
}: Props): ReactElement => {
  const stiffeningElements = useStiffeningStore(state => state.stiffeningIntervals)
  const graph = useTransmissionStore(state => state.tensileTransmissionGraph)
  const domains = useModelStore(state => state.domains)
  const walls = useModelStore(state => state.model.walls)

  // Unsaved edits live in the form only. The store changes on save, recalculation or a refetch
  // from the server, so the form follows it to always show what is persisted
  const defaultValues = useMemo<TensileTransmitterFormValues>(
    () => toTensileFormValues(graph, selectedElement),
    [graph, selectedElement],
  )

  const validationContext = useMemo(
    () => ({ stiffeningElements, domains, walls }),
    [stiffeningElements, domains, walls],
  )

  return (
    <Form2
      key={selectedElement}
      defaultValues={defaultValues}
      validationSchema={connectorListSchema}
      validationContext={validationContext}
      reinitialize
      // Submit the values as they are: positions are exact decimal strings from the server (or the
      // input). Converting them to floats moves e.g. 3.55 m to 3.5499… and the backend no longer
      // matches it to its stiffening interval
      onSubmit={values => onSave(applyTensileFormValues(graph, selectedElement, values))}
    >
      <TensileTransmitterListFields
        selectedElement={selectedElement}
        savedValues={defaultValues}
        onRecalculate={onRecalculate}
        isLoading={isLoading}
        isRecalculating={isRecalculating}
      />
    </Form2>
  )
}

export default TensileTransmitterList
